import React, { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  Space,
  Tag,
  Empty,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { toast } from "sonner";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import {
  createFileAttachement,
  deleteFileAttachementByID,
  getFileAttachementByEntityId,
} from "../../../../api/fileAttachement";
import {
  deleteHomework,
  getHomeworksAndAnswerByStudent,
} from "../../../../api/homework";

import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../../../core/common/autorisations/permissionsMap";
import {
  createHomeworkAnswer,
  updateHomeworkAnswer,
} from "../../../../api/homeworkAnswer";
import { openSupportInNewTab } from "../../../../helper/supportHelper";

// Types
interface Assignment {
  id: string;
  title: string;
  description: string;
  deadline: string;
  createdAt: string;
  status: "active" | "closed";
  attachments: any[];
  responses: any[];
}

interface StudentResponse {
  id: string;
  studentId: string;
  studentName: string;
  studentAvatar?: string;
  submission_date?: string;
  is_late?: boolean;
  remark?: string;
  observation?: string;
  files: any[];
  status?: "submitted" | "pending" | "late";
}

// Zod Schema
const responseSchema = z.object({
  remark: z.string(),
});

type ResponseFormData = z.infer<typeof responseSchema>;

interface Props {
  courseId: string;
  classes: any[];
}

const AssignmentsManager: React.FC<Props> = ({ courseId, classes }) => {
  // State
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingResponse, setEditingResponse] =
    useState<StudentResponse | null>(null);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<any | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  // Hook Form
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ResponseFormData>({
    resolver: zodResolver(responseSchema),
  });

  // Load mock data on mount
  useEffect(() => {
    fetchAssignments();
  }, []);

  // fetch assignments
  const fetchAssignments = async () => {
    try {
      setIsLoading(true);
      const response = await getHomeworksAndAnswerByStudent(courseId);

      // console.log("assignements: ", response.data);

      // charger les réponses étudiantes
      // setStudentResponses(response.data.answers);

      const formattedAssignments = await Promise.all(
        (response.data.homeworks || []).map(async (item: any) => {
          let responseFiles = [];

          if (item.HomeworkAnswers.length > 0) {
            responseFiles = await getFileAttachementByEntityId(
              item.HomeworkAnswers[0]?.id,
              "HomeworkAnswer",
            );
          }

          return {
            ...item,
            deadline: item.due_date,
            attachments: item.files || [],
            responses: item.HomeworkAnswers || [],
            responseFiles: responseFiles?.data || [],
            status: dayjs(item.due_date).isAfter(dayjs()) ? "active" : "closed",
          };
        }),
      );
      // console.log("assignement formatted: ", formattedAssignments);

      setAssignments(formattedAssignments);
    } catch (error: any) {
      // console.log("Erreur de chargement des devoirs : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Erreur de chargement des devoirs",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handlers
  const handleOpenCreate = () => {
    setEditingResponse(null);
    reset({ remark: "" });
    setFileList([]);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (studentResponse: any) => {
    setEditingResponse(studentResponse);
    setValue("remark", studentResponse.remark || "");
    setIsModalOpen(true);
  };

  const onSubmit = async (data: ResponseFormData) => {
    try {
      setIsLoading(true);

      const formData = new FormData();

      if (fileList.length > 0) {
        for (const file of fileList) {
          formData.append("files", file.originFileObj);
        }
      }

      if (editingResponse) {
        const res = await updateHomeworkAnswer(editingResponse.id, {
          remark: data.remark,
        });

        if (fileList.length > 0) {
          formData.append("entity_id", res.data.id);
          formData.append("entity_type", "HomeworkAnswer");

          await createFileAttachement(formData);
        }
        toast.success("Réponse modifié avec succès");
      } else {
        const res = await createHomeworkAnswer({
          id_homework: selectedAssignment?.id,
          remark: data.remark,
        });

        if (fileList.length > 0) {
          formData.append("entity_id", res.data.id);
          formData.append("entity_type", "HomeworkAnswer");

          await createFileAttachement(formData);
        }
        toast.success("Devoir/TP créé avec succès");
      }
      // recharger les devoirs après la création ou la modification
      fetchAssignments();
      setIsModalOpen(false);
    } catch (error: any) {
      // console.error("Erreur lors de la création du Devoir/Tp: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur est survenue",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewResponses = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setIsResponseModalOpen(true);
  };

  const handleDeleteAssignementFile = async (fileId: string) => {
    try {
      await deleteFileAttachementByID(
        fileId,
        buildPermissionContext(Resource.FILE_ATTACHEMENT, Action.DELETE),
      );
      toast.success("Fichier supprimé");
      fetchAssignments();
    } catch (error: any) {
      // console.log("Erreur lors de la suppression du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleGetAssignementFile = async (fileId: string, fileName: string) => {
    try {
      await openSupportInNewTab({ entityType: "HomeworkAnswer", fileName });
    } catch (error: any) {
      // console.log("Erreur lors de l'ouverture du fichier: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Columns for Table
  const columns = [
    {
      title: "Titre",
      dataIndex: "title",
      key: "title",
      render: (text: string, record: Assignment) => (
        <div>
          <div className="fw-bold">{text}</div>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{record.description}</Tooltip>}
          >
            <small className="text-muted">
              {record.description.substring(0, 50)}...
            </small>
          </OverlayTrigger>
        </div>
      ),
    },
    {
      title: "Date limite",
      dataIndex: "deadline",
      key: "deadline",
      render: (date: string) => (
        <Tag color={dayjs(date).isBefore(dayjs()) ? "red" : "green"}>
          {dayjs(date).format("DD/MM/YYYY HH:mm")}
        </Tag>
      ),
    },
    {
      title: "Fichiers",
      dataIndex: "attachments",
      key: "attachments",
      render: (files: any[], record: Assignment) => (
        <div>
          <span>{files.length} fichier(s)</span>
          {files.length > 0 && (
            <div className="mt-1">
              {files.map((file) => (
                <div key={file.id} className="d-flex align-items-center gap-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{file.fileOriginalName}</Tooltip>}
                  >
                    <div>
                      <Button
                        type="link"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() =>
                          handleGetAssignementFile(file.id, file.filename)
                        }
                      >
                        {file.fileOriginalName.length > 20
                          ? `${file.fileOriginalName.slice(0, 15)}…`
                          : file.fileOriginalName}
                      </Button>
                    </div>
                  </OverlayTrigger>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Assignment) =>
        record.responses.length > 0 ? (
          <Space>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Voir les réponses</Tooltip>}
            >
              <Button
                icon={<EyeOutlined />}
                onClick={() => handleViewResponses(record)}
              />
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Modifier</Tooltip>}
            >
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  handleOpenEdit(record?.responses[0]);
                }}
              />
            </OverlayTrigger>
            {/*<Popconfirm
              title="Êtes-vous sûr de vouloir supprimer cette réponse ?"
              onConfirm={() => handleDelete(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Button icon={<DeleteOutlined />} danger />
            </Popconfirm>*/}
          </Space>
        ) : (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Créer une réponse</Tooltip>}
          >
            <div className="w-100 d-flex justify-content-center">
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedAssignment(record);
                  handleOpenCreate();
                }}
              />
            </div>
          </OverlayTrigger>
        ),
    },
  ];

  useEffect(() => {
    if (!isModalOpen && fileList.length > 0) {
      // vider le fileList
      setFileList([]);
    }
  }, [isModalOpen]);

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Devoirs & TD</h4>
      </div>

      <Table
        columns={columns}
        dataSource={assignments}
        rowKey="id"
        locale={{ emptyText: <Empty description="Aucun devoir créé" /> }}
      />

      {/* Create/Edit Modal */}
      <Modal
        title={editingResponse ? "Modifier votre réponse" : "Créer une réponse"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={700}
      >
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Form.Item
            label="Noter vos remarques:"
            validateStatus={errors.remark ? "error" : ""}
            help={errors.remark?.message}
          >
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  rows={4}
                  placeholder="Remarque détaillées..."
                />
              )}
            />
          </Form.Item>

          <Form.Item label="Ajouter des fichiers joints">
            <Upload
              beforeUpload={() => false} // Prevent auto upload
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              multiple
            >
              <Button icon={<UploadOutlined />}>
                Sélectionner des fichiers
              </Button>
            </Upload>
          </Form.Item>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button onClick={() => setIsModalOpen(false)}>Annuler</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {editingResponse ? "Enregistrer" : "Créer"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* View Responses Modal */}
      <Modal
        title={`Réponses - ${selectedAssignment?.title}`}
        open={isResponseModalOpen}
        onCancel={() => setIsResponseModalOpen(false)}
        width={500}
        footer={[
          <Button key="close" onClick={() => setIsResponseModalOpen(false)}>
            Fermer
          </Button>,
        ]}
      >
        <div
          className="row row-cols-1 g-2"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {selectedAssignment?.responses?.map((response: any) => {
            const hasSubmission = !!response.submission_date;
            const isLate = !!response.is_late;
            const statusText = hasSubmission
              ? isLate
                ? "En retard"
                : "Rendu"
              : "Non rendu";
            const borderClass = hasSubmission
              ? isLate
                ? "border-warning"
                : "border-success"
              : "border-secondary";

            return (
              <div key={response.id} className="col">
                <div className={`card h-100 border-2 ${borderClass} shadow-sm`}>
                  <div className="card-body p-3 d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <Tag
                        color={
                          hasSubmission
                            ? isLate
                              ? "warning"
                              : "success"
                            : "default"
                        }
                      >
                        {statusText}
                      </Tag>
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.8rem" }}
                      >
                        {hasSubmission
                          ? dayjs(response.submission_date).format(
                              "DD/MM/YYYY HH:mm",
                            )
                          : "-"}
                      </span>
                    </div>
                    <div className="mt-auto d-flex justify-content-between gap-2">
                      {hasSubmission && (
                        <div className="d-flex flex-column gap-2">
                          <div>
                            <span className="fw-bold text-muted">
                              Votre remarque :
                            </span>
                            <p className="mb-0 mt-1">{response.remark}</p>
                          </div>
                          <div>
                            <span className="fw-bold text-muted">
                              Observation du Professeur :
                            </span>
                            <p className="mb-0 mt-1">
                              {response?.observation || "Aucune observation."}
                            </p>
                          </div>
                          <div>
                            <span className="fw-bold text-muted">
                              Fichiers :
                            </span>
                            <div>
                              <span>
                                {selectedAssignment?.responseFiles?.length}{" "}
                                fichier(s)
                              </span>
                              {selectedAssignment?.responseFiles?.length && (
                                <div className="mt-1">
                                  {selectedAssignment?.responseFiles?.map(
                                    (file: any) => (
                                      <div
                                        key={file.id}
                                        className="d-flex align-items-center gap-2"
                                      >
                                        <OverlayTrigger
                                          placement="top"
                                          overlay={
                                            <Tooltip>
                                              {file.fileOriginalName}
                                            </Tooltip>
                                          }
                                        >
                                          <div>
                                            <Button
                                              type="link"
                                              size="small"
                                              icon={<FileTextOutlined />}
                                              onClick={() =>
                                                handleGetAssignementFile(
                                                  file.id,
                                                  file.filename,
                                                )
                                              }
                                            >
                                              {file.fileOriginalName.length > 20
                                                ? `${file.fileOriginalName.slice(0, 15)}…`
                                                : file.fileOriginalName}
                                            </Button>
                                          </div>
                                        </OverlayTrigger>
                                        <Popconfirm
                                          title="Êtes-vous sûr de vouloir supprimer ce fichier ?"
                                          onConfirm={async () =>
                                            handleDeleteAssignementFile(file.id)
                                          }
                                          okText="Oui"
                                          cancelText="Non"
                                        >
                                          <Button
                                            type="text"
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                          />
                                        </Popconfirm>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {!selectedAssignment?.responses?.length && (
            <div className="col-12">
              <div className="text-center p-5 text-muted">
                Aucune réponse pour ce devoir.
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AssignmentsManager;
